import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, ShieldHalf, Sparkles } from "lucide-react";
import {
  BLOCKS,
  BLOCK_GROUPS,
  CLUB,
  FOCUS_BY_ID,
  FOCUS_OPTIONS,
  SPONSORS,
  SPONSOR_BY_ID,
  TARGET_OPTIONS,
  countTools,
  createSetup,
  euro,
} from "./demoData";
import {
  BTN_GHOST,
  BTN_PRIMARY,
  BTN_SECONDARY,
  EASE,
  GradientCheck,
  Kicker,
  Panel,
  QtyStepper,
  Segmented,
  Switch,
} from "./ui";

const STEPS = [
  { value: 0, label: "1 · Partner" },
  { value: 1, label: "2 · Leistungen" },
  { value: 2, label: "3 · Schwerpunkt" },
];

const SPECTATOR_OPTIONS = [
  { value: "erste", label: "1. Herren" },
  { value: "alle", label: "Alle Mannschaften" },
  { value: "aus", label: "Nicht erfassen" },
];

const StepTitle = ({ no, title, text }) => (
  <div className="mb-7">
    <Kicker>Schritt {no} von 3</Kicker>
    <h3 className="mt-3 font-display font-bold text-2xl md:text-3xl text-white">{title}</h3>
    {text && <p className="mt-2 max-w-xl text-sm md:text-base leading-relaxed text-white/55">{text}</p>}
  </div>
);

const SettingRow = ({ title, text, children, dashed = false }) => (
  <div
    className={`flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 rounded-2xl border px-5 py-4 ${
      dashed ? "border-dashed border-white/15" : "border-white/10"
    }`}
  >
    <div className="flex-1 min-w-0">
      <div className="text-sm font-semibold text-white">{title}</div>
      {text && <div className="mt-0.5 text-xs leading-relaxed text-white/45">{text}</div>}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

const BlockCard = ({ block, state, onPatch, sponsor }) => {
  const Icon = block.icon;
  const active = state.on;
  const hasControls = block.qty || block.qr || block.target || block.freeText;
  const short =
    block.id === "vorteil"
      ? `${sponsor.offerLabel}, nur für die ${CLUB.squad} Spieler der ${CLUB.team}`
      : block.short;

  return (
    <div
      className={`rounded-2xl border transition-colors duration-300 ${
        active ? "border-white/25 bg-white/[0.04]" : "border-white/10 hover:border-white/20"
      }`}
    >
      <button
        type="button"
        onClick={() => onPatch({ on: !active })}
        aria-pressed={active}
        className="w-full flex items-start gap-3.5 p-4 text-left"
      >
        <span
          className={`flex items-center justify-center w-9 h-9 shrink-0 rounded-xl border transition-colors duration-300 ${
            active ? "bg-white/[0.08] border-white/15" : "bg-white/[0.03] border-white/10"
          }`}
        >
          <Icon className={`w-4 h-4 ${active ? "text-brand-orange" : "text-white/40"}`} />
        </span>
        <span className="flex-1 min-w-0">
          <span className={`block text-sm font-semibold ${active ? "text-white" : "text-white/70"}`}>
            {block.title}
          </span>
          <span className="block mt-0.5 text-xs leading-relaxed text-white/40">{short}</span>
        </span>
        <span
          className={`mt-0.5 flex items-center justify-center w-5 h-5 shrink-0 rounded-full border transition-colors duration-300 ${
            active ? "border-transparent bg-origin-border bg-gradient-to-br from-brand-orange to-brand-blue" : "border-white/20"
          }`}
        >
          {active && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
        </span>
      </button>

      <AnimatePresence initial={false}>
        {active && hasControls && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="overflow-hidden"
          >
            <div className="flex flex-wrap items-center gap-2.5 px-4 pb-4 sm:pl-[3.6rem]">
              {block.qty && (
                <QtyStepper
                  value={state.qty}
                  min={block.qty.min}
                  max={block.qty.max}
                  step={block.qty.step}
                  label={block.qty.label}
                  onChange={(qty) => onPatch({ qty })}
                />
              )}
              {block.qr && (
                <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] py-1 pl-1 pr-3">
                  <Switch checked={state.qr} onChange={(qr) => onPatch({ qr })} label={`QR-Code für ${block.title}`} />
                  <span className="text-xs text-white/60">QR-Code</span>
                </span>
              )}
              {block.target && (
                <Segmented
                  size="sm"
                  layoutId={`demo-target-${block.id}`}
                  label="Ziel des Links"
                  options={TARGET_OPTIONS}
                  value={state.target}
                  onChange={(target) => onPatch({ target })}
                />
              )}
              {block.freeText && (
                <input
                  type="text"
                  maxLength={60}
                  value={state.label ?? sponsor.otherLabel}
                  onChange={(e) => onPatch({ label: e.target.value })}
                  placeholder="Was ist vereinbart?"
                  aria-label="Name der eigenen Leistung"
                  className="w-full rounded-xl border border-white/12 bg-white/[0.03] px-3 py-2 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-brand-blue/60 focus:bg-white/[0.06]"
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SummaryRow = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4">
    <dt className="text-white/45">{label}</dt>
    <dd className="font-semibold text-white text-right tabular-nums">{value}</dd>
  </div>
);

const Summary = ({ setup }) => {
  const sponsor = SPONSOR_BY_ID[setup.sponsorId];
  const tools = countTools(setup);
  const active = BLOCKS.filter((b) => setup.blocks[b.id].on);
  return (
    <Panel className="overflow-hidden p-6 md:p-7">
      <div className="pointer-events-none absolute -top-20 -right-16 w-56 h-56 rounded-full bg-brand-orange/15 blur-[90px]" />
      <div className="relative">
        <Kicker>Euer Sponsoring</Kicker>
        <div className="mt-3 font-display font-bold text-xl leading-snug text-white">
          {sponsor.name} <span className="text-white/30">×</span> {CLUB.name}
        </div>
        <dl className="mt-6 space-y-3 text-sm">
          <SummaryRow label="Volumen" value={euro(setup.volume)} />
          <SummaryRow label="Leistungen des Vereins" value={active.length} />
          <SummaryRow label="Vom Unternehmen" value={setup.inKind ? "Geld und Sachleistung" : "Geld"} />
          <SummaryRow label="QR-Codes" value={tools.qr} />
          <SummaryRow label="Kurzlinks" value={tools.links} />
          <SummaryRow label="Schwerpunkt" value={FOCUS_BY_ID[setup.focus].label} />
        </dl>
        <ul className="mt-6 pt-6 border-t border-white/10 flex flex-wrap gap-1.5">
          {active.map((b) => (
            <li key={b.id} className="rounded-full border border-white/10 px-2.5 py-1 text-[11px] text-white/60">
              {b.title}
            </li>
          ))}
          {active.length === 0 && <li className="text-xs text-white/40">Noch keine Leistung gewählt.</li>}
        </ul>
        <p className="mt-6 text-xs leading-relaxed text-white/35">
          Im echten Setup kommen Vertragsdetails und Zuständigkeiten dazu, und beide Seiten bestätigen das
          Sponsoring.
        </p>
      </div>
    </Panel>
  );
};

export const DemoSetup = ({ setup, onChange, onCreate }) => {
  const [step, setStep] = useState(0);
  const stepsRef = useRef(null);
  const anchorRef = useRef(null);

  // Beim Schrittwechsel zurück zum Schritt-Umschalter, sonst bleibt man unten
  // beim Weiter-Knopf stehen. Über den Anker, damit Lenis weich scrollt.
  const goStep = (next) => {
    setStep(next);
    const top = stepsRef.current?.getBoundingClientRect().top ?? 0;
    if (top < 80) anchorRef.current?.click();
  };
  const sponsor = SPONSOR_BY_ID[setup.sponsorId];
  const tools = countTools(setup);

  const set = (key, value) => onChange({ ...setup, [key]: value });
  const patchBlock = (id, patch) =>
    onChange({ ...setup, blocks: { ...setup.blocks, [id]: { ...setup.blocks[id], ...patch } } });
  const selectSponsor = (id) => {
    if (id !== setup.sponsorId) onChange(createSetup(id), { sponsorChanged: true });
  };

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
      <div className="lg:col-span-8">
        <Panel className="p-6 md:p-9">
          <div ref={stepsRef} id="demo-setup-steps" className="mb-9 scroll-mt-28">
            <a ref={anchorRef} href="#demo-setup-steps" className="sr-only" tabIndex={-1} aria-hidden="true">
              Zu den Schritten des Setups
            </a>
            <Segmented
              layoutId="demo-setup-step"
              label="Schritte des Setups"
              size="sm"
              options={STEPS}
              value={step}
              onChange={goStep}
            />
          </div>

          {/* Bewusst ohne AnimatePresence: Jeder Schritt blendet neu ein. */}
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
          >
            {step === 0 && (
              <>
                <StepTitle
                  no={1}
                  title="Wer sponsert wen?"
                  text="Wählt ein Beispiel-Unternehmen. Jedes bringt ein typisches Paket mit, das ihr im nächsten Schritt anpassen könnt."
                />
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {SPONSORS.map((s) => {
                    const Icon = s.icon;
                    const active = setup.sponsorId === s.id;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => selectSponsor(s.id)}
                        aria-pressed={active}
                        className={`relative h-full flex flex-col text-left rounded-2xl border p-5 transition-all duration-300 ${
                          active ? "border-white/30 bg-white/[0.05]" : "border-white/10 hover:border-white/20"
                        }`}
                      >
                        <div className="w-full flex items-start justify-between gap-3">
                          <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-white/[0.06] border border-white/10">
                            <Icon className="w-[18px] h-[18px] text-brand-orange" />
                          </span>
                          {active && <GradientCheck />}
                        </div>
                        <div className="mt-4 font-display font-bold text-lg text-white">{s.name}</div>
                        <div className="mt-1 text-xs leading-relaxed text-white/45">{s.branch}</div>
                        <div className="mt-auto pt-4 text-[10px] uppercase tracking-[0.12em] font-bold text-white/35">
                          Schwerpunkt {FOCUS_BY_ID[s.preset.focus].label}
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-4 flex items-center gap-4 rounded-2xl border border-white/10 px-5 py-4">
                  <span className="flex items-center justify-center w-10 h-10 shrink-0 rounded-xl bg-white/[0.06] border border-white/10">
                    <ShieldHalf className="w-[18px] h-[18px] text-brand-blue" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/35">Verein</div>
                    <div className="font-display font-bold text-white">{CLUB.name}</div>
                    <div className="text-xs text-white/45">{CLUB.meta}</div>
                  </div>
                  <span className="ml-auto hidden sm:block text-xs text-white/30">in der Demo fest</span>
                </div>

                <div className="mt-8">
                  <div className="flex items-baseline justify-between gap-4">
                    <label htmlFor="demo-volume" className="text-sm font-semibold text-white">
                      Volumen für die Saison
                    </label>
                    <span className="font-display font-bold text-2xl tabular-nums text-white">
                      {euro(setup.volume)}
                    </span>
                  </div>
                  <input
                    id="demo-volume"
                    type="range"
                    min={500}
                    max={10000}
                    step={100}
                    value={setup.volume}
                    onChange={(e) => set("volume", Number(e.target.value))}
                    className="mt-3 w-full accent-brand-orange cursor-pointer"
                  />
                  <div className="mt-1 flex justify-between text-[11px] text-white/35">
                    <span>500 €</span>
                    <span>10.000 €</span>
                  </div>
                </div>

                <div className="mt-6">
                  <SettingRow title="Zusätzlich eine Sachleistung" text={sponsor.inKindLabel}>
                    <Switch checked={setup.inKind} onChange={(v) => set("inKind", v)} label="Sachleistung" />
                  </SettingRow>
                </div>
              </>
            )}

            {step === 1 && (
              <>
                <StepTitle
                  no={2}
                  title="Was gehört zum Sponsoring?"
                  text="Schaltet an, was vereinbart ist. QR-Codes und Kurzlinks erzeugt MatchGate automatisch, und danach richtet sich, was der Verein später einträgt."
                />
                <div className="space-y-9">
                  {BLOCK_GROUPS.map((group) => (
                    <div key={group.id}>
                      <div className="flex items-baseline gap-3 mb-3">
                        <h4 className="font-display font-bold text-lg text-white">{group.label}</h4>
                        {group.hint && <span className="text-xs text-white/35">{group.hint}</span>}
                      </div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {BLOCKS.filter((b) => b.group === group.id).map((block) => (
                          <BlockCard
                            key={block.id}
                            block={block}
                            state={setup.blocks[block.id]}
                            sponsor={sponsor}
                            onPatch={(patch) => patchBlock(block.id, patch)}
                          />
                        ))}
                      </div>
                      {group.id === "spieltag" && (
                        <div className="mt-3">
                          <SettingRow
                            dashed
                            title="Zuschauer erfassen"
                            text="Grundlage für die Hochrechnung der Kontakte vor Ort"
                          >
                            <Segmented
                              size="sm"
                              layoutId="demo-spectators"
                              label="Zuschauer erfassen"
                              options={SPECTATOR_OPTIONS}
                              value={setup.spectators}
                              onChange={(v) => set("spectators", v)}
                            />
                          </SettingRow>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {tools.qr > 0 && (
                  <div className="mt-9">
                    <SettingRow
                      title="Wohin führen die QR-Codes?"
                      text="Je Platzierung entsteht ein eigener Code, alle führen auf dasselbe Ziel."
                    >
                      <Segmented
                        size="sm"
                        layoutId="demo-qr-target"
                        label="Ziel der QR-Codes"
                        options={TARGET_OPTIONS}
                        value={setup.qrTarget}
                        onChange={(v) => set("qrTarget", v)}
                      />
                    </SettingRow>
                  </div>
                )}
              </>
            )}

            {step === 2 && (
              <>
                <StepTitle
                  no={3}
                  title="Worauf legt ihr den größten Wert?"
                  text="Der Schwerpunkt bestimmt, welche Zahlen im Dashboard und im Bericht oben stehen. Erfasst wird trotzdem alles."
                />
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {FOCUS_OPTIONS.map((f) => {
                    const Icon = f.icon;
                    const active = setup.focus === f.id;
                    return (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => set("focus", f.id)}
                        aria-pressed={active}
                        className={`flex items-start gap-4 rounded-2xl border p-5 text-left transition-all duration-300 ${
                          active ? "border-white/30 bg-white/[0.05]" : "border-white/10 hover:border-white/20"
                        }`}
                      >
                        <span className="flex items-center justify-center w-10 h-10 shrink-0 rounded-xl bg-white/[0.06] border border-white/10">
                          <Icon className={`w-[18px] h-[18px] ${active ? "text-brand-orange" : "text-white/45"}`} />
                        </span>
                        <span className="flex-1">
                          <span className="block font-display font-bold text-lg text-white">{f.label}</span>
                          <span className="block mt-1 text-xs leading-relaxed text-white/45">{f.text}</span>
                        </span>
                        {active && <GradientCheck />}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-6">
                  <SettingRow
                    title="Ergebnisse für den Verein sichtbar"
                    text="Einlösungen, Bewerbungen und Abschlüsse, die ihr meldet, sieht auch der Verein."
                  >
                    <Switch
                      checked={setup.shareResults}
                      onChange={(v) => set("shareResults", v)}
                      label="Ergebnisse für den Verein sichtbar"
                    />
                  </SettingRow>
                </div>
              </>
            )}
          </motion.div>

          <div className="mt-10 pt-6 border-t border-white/10 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
            {step > 0 ? (
              <button type="button" onClick={() => goStep(step - 1)} className={BTN_SECONDARY}>
                <ArrowLeft className="w-4 h-4" />
                Zurück
              </button>
            ) : (
              <button type="button" onClick={onCreate} className={BTN_GHOST}>
                Direkt zum fertigen Beispiel
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
            {step < 2 ? (
              <button type="button" onClick={() => goStep(step + 1)} className={BTN_PRIMARY}>
                Weiter
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-300" />
              </button>
            ) : (
              <button type="button" onClick={onCreate} className={BTN_PRIMARY}>
                Dashboard erstellen
                <Sparkles className="w-4 h-4" />
              </button>
            )}
          </div>
        </Panel>
      </div>

      <div className="lg:col-span-4">
        <div className="lg:sticky lg:top-sticky">
          <Summary setup={setup} />
        </div>
      </div>
    </div>
  );
};
